import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { MdArrowForward } from 'react-icons/md';

// Building blocks shared by the public home page and its editor (Sprint 7 "Home Template").
// With ctx.edit false they render plain page markup. With ctx.edit true they render the same
// spot as a real, labelled form field styled like the page text: unlike contentEditable,
// screen readers announce it as an edit field, and pasted formatting can't get in.
//
// ctx: { edit, content, onChange(path, value), errorFor(path), onUploadImage(path, file), uploading(path) }

export const fieldId = (path) => `lp-${path.replace(/\./g, '-')}`;

export const getAt = (obj, path) => path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

export const setAt = (obj, path, value) => {
  const [key, ...rest] = path.split('.');
  const copy = Array.isArray(obj) ? [...obj] : { ...obj };
  copy[key] = rest.length ? setAt(obj[key] ?? {}, rest.join('.'), value) : value;
  return copy;
};

const describedBy = (...ids) => ids.filter(Boolean).join(' ') || undefined;

/** Site path → router link; in-page anchor or outside address → plain link. */
export function SmartLink({ url, className, children, onClick }) {
  if (url.startsWith('/')) {
    return <Link to={url} className={className} onClick={onClick}>{children}</Link>;
  }
  return <a href={url} className={className} onClick={onClick}>{children}</a>;
}

/** h1–h6 from a number, so the editor can render the page one level down. */
export function Heading({ level, children, ...rest }) {
  const Tag = `h${Math.min(Math.max(level, 1), 6)}`;
  return <Tag {...rest}>{children}</Tag>;
}

function FieldError({ id, message }) {
  if (!message) return null;
  return <span id={id} className="hpe-error"><span className="sr-only">Error: </span>{message}</span>;
}

/** Editable text. `as`/className style the visitor view; the edit field inherits the same type. */
export function EText({ ctx, path, label, as: Tag = 'p', className = '', multiline = false, placeholder, children }) {
  const value = getAt(ctx.content, path) ?? '';
  if (!ctx.edit) {
    if (!value) return null;
    return <Tag className={className}>{children ? children(value) : value}</Tag>;
  }
  const id = fieldId(path);
  const error = ctx.errorFor(path);
  const Input = multiline ? 'textarea' : 'input';
  return (
    <div className={`hpe-field ${className}`}>
      <label htmlFor={id} className="hpe-tag">{label}</label>
      <Input
        id={id}
        className={`hpe-input${multiline ? ' hpe-input--multi' : ''}`}
        type={multiline ? undefined : 'text'}
        rows={multiline ? Math.max(2, Math.ceil(String(value).length / 60)) : undefined}
        value={value}
        placeholder={placeholder}
        onChange={(e) => ctx.onChange(path, e.target.value)}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy(error && `${id}-error`)}
      />
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

/** Link or button: editable text plus its address. Hidden for visitors when either is empty. */
export function ELink({ ctx, path, name, className, labelKey = 'label', urlKey = 'url', icon = true }) {
  const labelPath = `${path}.${labelKey}`;
  const urlPath = `${path}.${urlKey}`;
  const label = getAt(ctx.content, labelPath) || '';
  const url = getAt(ctx.content, urlPath) || '';
  if (!ctx.edit) {
    if (!label || !url) return null;
    return (
      <SmartLink url={url} className={className}>
        {label}
        {icon && <MdArrowForward aria-hidden="true" className="hp-btn-icon" />}
      </SmartLink>
    );
  }
  const labelId = fieldId(labelPath);
  const urlId = fieldId(urlPath);
  const labelError = ctx.errorFor(labelPath);
  const urlError = ctx.errorFor(urlPath);
  return (
    <div className="hpe-linkgroup">
      <label htmlFor={labelId} className="hpe-tag">{name} text</label>
      <div className={className}>
        <input
          id={labelId}
          type="text"
          className="hpe-input hpe-input--button"
          value={label}
          placeholder="Empty: hidden"
          onChange={(e) => ctx.onChange(labelPath, e.target.value)}
          aria-invalid={labelError ? 'true' : undefined}
          aria-describedby={describedBy(labelError && `${labelId}-error`)}
        />
      </div>
      <FieldError id={`${labelId}-error`} message={labelError} />
      <label htmlFor={urlId} className="hpe-tag hpe-tag--sub">{name} link</label>
      <input
        id={urlId}
        type="text"
        className="hpe-input hpe-input--url"
        value={url}
        placeholder="/register, #upcoming-events or https://…"
        onChange={(e) => ctx.onChange(urlPath, e.target.value)}
        aria-invalid={urlError ? 'true' : undefined}
        aria-describedby={describedBy(`${urlId}-hint`, urlError && `${urlId}-error`)}
      />
      <span id={`${urlId}-hint`} className="sr-only">A page on this site starting with /, a home page section starting with #, or a full address starting with https://</span>
      <FieldError id={`${urlId}-error`} message={urlError} />
    </div>
  );
}

/** Checklist, edited one point per line. */
export function EList({ ctx, path, label, className }) {
  const items = getAt(ctx.content, path) || [];
  if (!ctx.edit) {
    const shown = items.filter((b) => b && b.trim());
    if (!shown.length) return null;
    return (
      <ul className={className}>
        {shown.map((b, i) => <li key={`${i}-${b}`}>{b}</li>)}
      </ul>
    );
  }
  const id = fieldId(path);
  const error = ctx.errorFor(path);
  return (
    <div className="hpe-field">
      <label htmlFor={id} className="hpe-tag">{label}</label>
      <span id={`${id}-hint`} className="hpe-hint">One point per line, up to 6.</span>
      <textarea
        id={id}
        className="hpe-input hpe-input--multi"
        rows={Math.max(3, items.length + 1)}
        value={items.join('\n')}
        onChange={(e) => ctx.onChange(path, e.target.value.split('\n'))}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy(`${id}-hint`, error && `${id}-error`)}
      />
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

/**
 * Image with upload, replace and remove. `altPath`/`decorativePath` add the description
 * controls; without them the image is named by its surroundings (e.g. the logo link).
 */
export function EImage({ ctx, path, label, altPath, decorativePath, className, emptyText }) {
  const inputRef = useRef(null);
  const url = getAt(ctx.content, path) || '';
  const id = fieldId(path);
  const error = ctx.errorFor(path);
  const uploading = ctx.uploading?.(path);
  const decorative = decorativePath ? !!getAt(ctx.content, decorativePath) : false;
  const alt = altPath ? getAt(ctx.content, altPath) || '' : '';

  if (!ctx.edit) {
    if (!url) return null;
    return <img src={url} alt={decorative ? '' : alt} className={className} />;
  }

  return (
    <div className="hpe-image" role="group" aria-labelledby={`${id}-label`}>
      <span id={`${id}-label`} className="hpe-tag">{label}</span>
      {url ? (
        <img src={url} alt={`Current ${label.toLowerCase()}`} className={className} />
      ) : (
        <p className="hpe-image-empty">{emptyText || 'No image yet.'}</p>
      )}
      <div className="hpe-image-actions">
        <button
          type="button"
          id={id}
          className="hpe-button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          aria-describedby={describedBy(`${id}-types`, error && `${id}-error`)}
        >
          {uploading ? 'Uploading…' : `${url ? 'Replace' : 'Upload'} ${label.toLowerCase()}`}
        </button>
        {url && (
          <button type="button" className="hpe-button" onClick={() => ctx.onChange(path, '')}>
            Remove {label.toLowerCase()}
          </button>
        )}
        <span id={`${id}-types`} className="hpe-hint">PNG, JPG, GIF, WebP or SVG.</span>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,.svg"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) ctx.onUploadImage(path, file);
            e.target.value = '';
          }}
        />
      </div>
      <FieldError id={`${id}-error`} message={error} />
      {url && decorativePath && (
        <div className="hpe-check">
          <input
            id={fieldId(decorativePath)}
            type="checkbox"
            checked={decorative}
            onChange={(e) => ctx.onChange(decorativePath, e.target.checked)}
          />
          <label htmlFor={fieldId(decorativePath)}>Decorative: screen readers skip it</label>
        </div>
      )}
      {url && altPath && !decorative && (
        <EText ctx={ctx} path={altPath} label="Image description (alt text)" multiline
          placeholder="What the image shows, for people who can't see it" />
      )}
    </div>
  );
}
